import * as React from 'react'
import styled from 'styled-components'
import * as constants from 'static/constants'
import { agentService } from 'core/services'
import { Spinner, Button } from 'components/atoms'
import Guide from 'components/organisms/Tours/Weekly/Drawer/Guide'
import GuideSearch from 'components/organisms/Guides/GuideSearch'
import { IBuildedTour } from 'utils/weeklyTypes'
import { useTourDrawerContext } from 'core/contexts/tour/TourDrawerContext'

interface IProps {
  currentUser: any
  tour: IBuildedTour
}

export const GuidePanel: React.VFC<IProps> = React.memo(({ currentUser, tour }) => {
  const {
    groupSelectingResource,
    setGroupSelectingResource,
    showGuides,
    setShowGuides,
    guideFilterOptions,
    setGuideFilterOptions,
  } = useTourDrawerContext()

  const [currentGuides, setCurrentGuides] = React.useState([])
  const [hasMore, setHasMore] = React.useState(false)
  const [page, setPage] = React.useState(1)
  const [loading, setLoading] = React.useState(false)

  // 多重読み込みの防止およびobserverの初期化を1度で済ませるためのref
  const hasMoreRef = React.useRef(hasMore)
  const observerRef = React.useRef<IntersectionObserver | null>(null)
  const observerTargetRef = React.useRef(null)
  const isLoadingRef = React.useRef(false)

  const sleep = (sec: number) => new Promise(resolve => setTimeout(resolve, sec * 1000))

  const searchGuides = React.useCallback(
    async _page => {
      try {
        setLoading(true)
        await sleep(0.5)

        const { guides: fetchedGuides, pagination } = await agentService.getGuides(
          guideFilterOptions,
          tour,
          _page
        )

        if (_page === 1) {
          setCurrentGuides(fetchedGuides)
        } else {
          setCurrentGuides(old => [...old, ...fetchedGuides])
        }
        setHasMore(!!pagination.next_page)
      } catch (e) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    },
    [agentService.getGuides, tour, guideFilterOptions]
  )

  const handleAfterUpdateGuide = React.useCallback(
    (guide, params) => {
      const index = currentGuides.findIndex(cg => cg.id === guide.id)
      if (index !== -1) {
        setCurrentGuides(prevGuides => [
          ...prevGuides.slice(0, index),
          { ...prevGuides[index], ...params },
          ...prevGuides.slice(index + 1),
        ])
      }
    },
    [currentGuides]
  )

  React.useEffect(() => {
    setGroupSelectingResource(null)
  }, [tour])

  React.useEffect(() => {
    setHasMore(false)
    setCurrentGuides([])
  }, [guideFilterOptions])

  // observer用のref更新
  React.useEffect(() => {
    hasMoreRef.current = hasMore
  }, [hasMore])

  React.useEffect(() => {
    isLoadingRef.current = loading
  }, [loading])

  React.useEffect(() => {
    observerRef.current = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting && hasMoreRef.current && !isLoadingRef.current) {
          setPage(p => p + 1)
        }
      },
      { threshold: 1.0 }
    )

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
    }
  }, [])

  React.useEffect(() => {
    if (observerRef.current && observerTargetRef.current) {
      observerRef.current.observe(observerTargetRef.current)
    }

    return () => {
      if (observerRef.current && observerTargetRef.current) {
        observerRef.current.unobserve(observerTargetRef.current)
      }
    }
  }, [observerTargetRef.current])

  const handleConfirm = () => {
    setShowGuides(true)
  }

  return (
    <Wrapper>
      <div className="title">
        <p>ガイド検索</p>
        <Button
          small={true}
          handleClick={() => setShowGuides(current => !current)}
          disabled={loading || currentGuides.length === 0}
        >
          ガイドを{showGuides ? '隠す' : '表示する'}
        </Button>
      </div>
      <GuideSearch
        searchGuides={searchGuides}
        filterOptions={guideFilterOptions}
        setFilterOptions={setGuideFilterOptions}
        page={page}
        setPage={setPage}
        onConfirm={handleConfirm}
      />
      <div className="guideListContainer">
        {!loading && !hasMore && currentGuides.length === 0 && (
          <div className="emptyMessage">ツアーに紐づいたガイドが存在しません</div>
        )}
        {!loading && !showGuides && currentGuides.length > 0 && (
          <div className="emptyMessage">
            ガイドが非表示になっています（{currentGuides.length}件）
            <br />
            <span>
              <a
                onClick={e => {
                  e.preventDefault()
                  setShowGuides(true)
                }}
                href="#"
              >
                表示ボタン
              </a>
              をクリック、もしくは検索をすると
              <br />
              自動で表示状態に切り替わります
            </span>
          </div>
        )}
        {showGuides &&
          currentGuides.length > 0 &&
          currentGuides.map(guide => (
            <Guide
              currentUser={currentUser}
              guide={guide}
              showDetail={true}
              handleAfterUpdateGuide={handleAfterUpdateGuide}
              tourGuide={tour.tour_guides.find(tg => tg.guide_id === guide.id)}
              setGroupSelectingResource={setGroupSelectingResource}
              groupSelectingResource={groupSelectingResource}
              conflictingTourIds={guide.conflicting_tour_ids}
              tour={tour}
              key={guide.id}
            />
          ))}
        <div ref={observerTargetRef}>
          {showGuides && loading && (
            <Loader>
              <Spinner />
            </Loader>
          )}
        </div>
      </div>
    </Wrapper>
  )
})

const Loader = styled.div`
  padding: 16px;
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${constants.COLORS.background2};
  // 共通コンポーネント GuideSearch の検索ボタンを、このパネル内でのみ白背景にする
  .button.filter {
    background-color: ${constants.COLORS.white};
  }
  .title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    line-height: 24px;
    margin-bottom: 4px;
  }

  .guideListContainer {
    position: relative;
    flex: 1 1 0;
    padding: 0 16px 40px;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .emptyMessage {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    padding: 0 16px 64px;
    color: ${constants.COLORS.subText};
    font-size: 14px;
    text-align: center;
    span {
      display: inline-block;
      font-size: 12px;
      margin-top: 8px;
    }
    a {
      color: ${constants.COLORS.primary};
      text-decoration: underline;
      &:hover {
        text-decoration: none;
      }
    }
  }
`
